<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { RouterLink } from 'vue-router'
import ButtonSeed from '@/design/primitives/ButtonSeed.vue'
import { api } from '@/api/client'
import { EP } from '@/api/endpoints'

const visible = ref(false)
const showCustomize = ref(false)
const cookiePolicyTitle = ref('Cookie Policy')
const KEY = 'evoluxu-cookie-ack-v2'

const prefs = ref({
  essential: true,
  functional: false,
  analytics: false,
  advertising: false
})

onMounted(async () => {
  try { if (!localStorage.getItem(KEY)) visible.value = true } catch {}
  try {
    const { data } = await api.get(EP.page('cookies'))
    if (typeof data?.title === 'string' && data.title.trim()) {
      cookiePolicyTitle.value = data.title.trim()
    }
  } catch {}
})

function save(p) {
  try { localStorage.setItem(KEY, JSON.stringify(p)) } catch {}
  window.dispatchEvent(new Event('cookie-consent-changed'))
  visible.value = false
}

function acceptAll() {
  save({ essential: true, functional: true, analytics: true, advertising: true })
}

function rejectAll() {
  save({ essential: true, functional: false, analytics: false, advertising: false })
}

function saveAndExit() {
  save({ ...prefs.value, essential: true })
}

function openCustomize() {
  showCustomize.value = true
}
const reopen = () => { try { prefs.value = {...prefs.value,...JSON.parse(localStorage.getItem(KEY)||'{}'),essential:true} } catch {} visible.value=true; showCustomize.value=true }
onMounted(()=>window.addEventListener('open-cookie-settings',reopen))
onUnmounted(()=>window.removeEventListener('open-cookie-settings',reopen))
</script>

<template>
  <Transition name="rise">
    <div v-if="visible" class="cookie">
      <template v-if="!showCustomize">
        <h2 class="cookie__title">Cookies Are Here</h2>
        <p class="cookie__text">
          We use cookies to enhance your experience and ensure that our Website functions properly. Essential cookies are necessary for basic operations and are set by default. We also employ additional cookies for various purposes that enhance your experience but are not required for core functions. You can adjust your cookie settings at any time. To learn more about how we use cookies please visit our
          <RouterLink to="/legal/cookies">{{ cookiePolicyTitle }}</RouterLink>.
        </p>

        <div class="cookie__actions">
          <ButtonSeed size="sm" variant="primary" @click="acceptAll">Accept All</ButtonSeed>
          <ButtonSeed size="sm" variant="ghost" @click="rejectAll">Reject All Nonessential Cookies</ButtonSeed>
          <ButtonSeed size="sm" variant="ghost" @click="openCustomize">Customize</ButtonSeed>
        </div>
      </template>

      <template v-else>
        <div class="cookie__settings">
          <div class="cookie__row">
            <div class="cookie__row-info">
              <span class="cookie__row-name">Essential Cookies (Always Active)</span>
              <span class="cookie__row-desc">These cookies are technically required for our Website to operate securely and correctly. They enable core features like user login, page security, and privacy preferences, and cannot be turned off.</span>
            </div>
            <label class="toggle toggle--locked">
              <input type="checkbox" :checked="true" disabled />
              <span class="toggle__track"></span>
            </label>
          </div>

          <div class="cookie__row">
            <div class="cookie__row-info">
              <span class="cookie__row-name">Functional (Preference) Cookies</span>
              <span class="cookie__row-desc">These cookies personalize your browsing experience by remembering your custom preferences, such as your preferred language and regional settings, so you do not have to re-enter them on future visits.</span>
            </div>
            <label class="toggle">
              <input type="checkbox" v-model="prefs.functional" />
              <span class="toggle__track"></span>
            </label>
          </div>

          <div class="cookie__row">
            <div class="cookie__row-info">
              <span class="cookie__row-name">Performance (Analytics) Cookies</span>
              <span class="cookie__row-desc">These cookies collect anonymous, aggregated data to show us how visitors navigate our Website. We use this information to measure page performance, diagnose technical errors, and improve our services.</span>
            </div>
            <label class="toggle">
              <input type="checkbox" v-model="prefs.analytics" />
              <span class="toggle__track"></span>
            </label>
          </div>

          <div class="cookie__row">
            <div class="cookie__row-info">
              <span class="cookie__row-name">Advertising/Targeting Cookies</span>
              <span class="cookie__row-desc">These cookies used to deliver more relevant content or ads and to measure the effectiveness of campaigns.</span>
            </div>
            <label class="toggle">
              <input type="checkbox" v-model="prefs.advertising" />
              <span class="toggle__track"></span>
            </label>
          </div>
        </div>

        <div class="cookie__actions cookie__actions--center">
          <ButtonSeed size="sm" variant="primary" @click="saveAndExit">Save &amp; Exit</ButtonSeed>
        </div>
      </template>
    </div>
  </Transition>
</template>

<style scoped>
.cookie {
  position: fixed;
  bottom: var(--sp-4);
  left: var(--sp-4);
  right: var(--sp-4);
  max-width: 940px;
  margin: 0 auto;
  background: rgba(11, 10, 8, 0.94);
  border: 1px solid var(--gold);
  border-radius: var(--r-sm);
  box-shadow: var(--shadow-3);
  padding: var(--sp-4) var(--sp-6);
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  z-index: 60;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  max-height: 85vh;
  overflow-y: auto;
}

.cookie__title {
  margin: 0;
  font-size: var(--fs-18, 18px);
  font-weight: 700;
  color: var(--ink);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.cookie__text {
  margin: 0;
  font-size: var(--fs-14);
  line-height: 1.5;
  color: var(--ink-soft);
}
.cookie__text a { color: var(--gold); text-decoration: underline; }

.cookie__actions {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  flex-wrap: wrap;
}

.cookie__actions--center {
  justify-content: center;
}

/* Settings panel */
.cookie__settings {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
}

.cookie__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-4);
  padding: var(--sp-2) var(--sp-3);
  background: rgba(229, 199, 107, 0.04);
  border: 1px solid rgba(229, 199, 107, 0.12);
  border-radius: var(--r-xs);
}

.cookie__row-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.cookie__row-name {
  font-size: var(--fs-14);
  font-weight: 600;
  color: var(--ink);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.cookie__row-desc {
  font-size: var(--fs-12);
  color: var(--ink-soft);
  line-height: 1.4;
}

/* Toggle switch */
.toggle {
  position: relative;
  display: inline-block;
  width: 44px;
  min-width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle__track {
  position: absolute;
  inset: 0;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid var(--ash-strong);
  border-radius: 24px;
  transition: background var(--t-fast) var(--ease-road), border-color var(--t-fast) var(--ease-road);
}

.toggle__track::before {
  content: "";
  position: absolute;
  height: 16px;
  width: 16px;
  left: 3px;
  top: 50%;
  transform: translateY(-50%);
  background: var(--ink-soft);
  border-radius: 50%;
  transition: transform var(--t-fast) var(--ease-road), background var(--t-fast) var(--ease-road);
}

.toggle input:checked + .toggle__track {
  background: var(--gold);
  border-color: var(--gold);
}

.toggle input:checked + .toggle__track::before {
  transform: translateY(-50%) translateX(20px);
  background: var(--ink-inverse);
}

.toggle--locked .toggle__track {
  background: var(--gold);
  border-color: var(--gold);
  opacity: 0.55;
  cursor: not-allowed;
}

.toggle--locked .toggle__track::before {
  transform: translateY(-50%) translateX(20px);
  background: var(--ink-inverse);
}

@media (max-width: 768px) {
  .cookie {
    padding: var(--sp-3) var(--sp-4);
  }
  .cookie__text { font-size: var(--fs-12); }
  .cookie__row-desc { font-size: var(--fs-12); }
}

.rise-enter-active, .rise-leave-active {
  transition: transform var(--t-base) var(--ease-cinema), opacity var(--t-base) var(--ease-cinema);
}
.rise-enter-from, .rise-leave-to { transform: translateY(20px); opacity: 0; }
</style>
